/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { Select } from '@glowforge/ui-common/components';
import VideoThumbnail from './video-thumbnail';

// @ts-ignore
import GridViewIcon from './icons/grid-view.svg';
// @ts-ignore
import ListViewIcon from './icons/list-view.svg';

import './video-gallery.less';

// eslint-disable-next-line max-len
/** @typedef {import('../../api/contentful-videos').VideoEntryCollection} VideoEntryCollection */

/**
 * Renders a list of video thumbnails with tag filters and layout options
 * @param {object} props
 * @param {string} props.gallery
 * @param {VideoEntryCollection} props.videos
 * @param {string[]} props.tags
 * @param {string[]} props.selectedTags
 * @param {string} [props.layout='grid']
 */
const VideoGallery = ({
  gallery,
  videos,
  tags,
  selectedTags,
  layout = 'grid',
}) => {
  const router = useRouter();

  const getCurrentParams = () => {
    const params = new URLSearchParams();
    if (selectedTags) params.set('tags', selectedTags.join(','));
    if (layout) params.set('layout', layout);
    return params;
  };

  const getFilterHref = (filter, value) => {
    const params = getCurrentParams();
    if (value) {
      params.set(filter, value);
    } else {
      params.delete(filter);
    }
    const search = params.toString();
    return `/v/${gallery}${search ? '?' : ''}${search}`;
  };

  const onChange = useCallback((option) => {
    const params = getCurrentParams();
    params.set('tags', option.value);
    router.push(`/v/${gallery}?${params.toString()}`);
  }, [selectedTags, layout]);

  const options = tags.map((tag) => ({
    label: tag,
    value: tag,
  }));

  options.unshift({
    label: 'All Videos',
    value: '',
  });

  return (
    <div className='VideoGallery'>
      <Select
        autosize={false}
        className='select'
        label='Filter videos by:'
        value={selectedTags[0] || ''}
        placeholder='Make a selection'
        onChange={onChange}
        clearable={false}
        searchable={false}
        options={options} />
      <div className='filters'>
        <div className='filter'>
          <div className='filters-title'>Filter videos by:</div>
          <div className='tag-links'>
            <Link
              href={getFilterHref('tags', null)}
              scroll={false}>
              <a
                className={classNames(
                  'clearLink tag-link',
                  { selected: selectedTags.length === 0 },
                )}>
                All
              </a>
            </Link>
            {tags.map((tag) => (
              <Link
                key={tag}
                href={getFilterHref('tags', tag)}
                scroll={false}>
                <a className={classNames(
                  'clearLink tag-link',
                  { selected: selectedTags.includes(tag) },
                )}>
                  {tag}
                </a>
              </Link>
            ))}
          </div>
        </div>
        <div className='filter'>
          <div className='filters-title'>View preference:</div>
          <div className='layout-links'>
            <Link
              href={getFilterHref('layout', 'grid')}
              scroll={false}>
              <a className={classNames(
                'layout-link',
                { selected: layout === 'grid' },
              )}>
                <GridViewIcon />
              </a>
            </Link>
            <Link
              href={getFilterHref('layout', 'list')}
              scroll={false}>
              <a className={classNames(
                'layout-link',
                { selected: layout === 'list' },
              )}>
                <ListViewIcon />
              </a>
            </Link>
          </div>
        </div>
      </div>
      <div className={classNames('video-thumbnails', layout)}>
        <AnimatePresence>
          {videos.items.map((video) => (
            <VideoThumbnail
              animated
              size={layout === 'grid' ? 'small' : 'large'}
              key={video?.fields?.slug}
              video={video} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VideoGallery;
